// Packages
import { Box, Typography } from "@mui/material";
import { cloneDeep } from "lodash";
import dayjs from "dayjs";

// Utils
import { getHwVersionLabel } from '../helper';

// Components
import CheckboxColumn from "components/Table/CheckboxColumn";
import { MenuSelect } from "components/Select/menuSelect";
import EllipsisTooltipCell from 'utils/ellipsisTooltipCell';

// Img
import { ReactComponent as DownloadIcon } from "assets/images/icons/icn_table_cell_download_fw.svg";

export const productCategories = {
  ap: {
    value: "ap",
    label: "Access Point"
  },
  switch: {
    value: "switch",
    label: "Switch",
  },
  router: {
    value: "router",
    label: "Router",
  },
}

export const getMainTableColumns = ({
  auth,
  columnHelper,
  permissions,
  theme,
  callbacks
}) => {
  return [
    permissions && (permissions === "Admin" || permissions === "Super Admin") &&
    CheckboxColumn(columnHelper, null, {}),
    (
      auth.scopeLevel !== "site" &&
      columnHelper.accessor(row => row.siteName ?? "-", {
        id: "siteName",
        header: "Site",
        cell: (info) => <>{info.getValue()}</>,
      })
    ),
    columnHelper.accessor("deviceType", {
      id: "deviceType",
      header: "Product Category",
      cell: (info) => {
        const value = info.getValue();
        const productCategory = productCategories?.[value]?.label ?? value ?? "-";
        return <>{productCategory}</>
      },
    }),
    columnHelper.accessor(row => row.seriesName ?? "-", {
      id: "seriesName",
      header: "Series Name",
      cell: (info) => <>{info.getValue()}</>,
    }),
    columnHelper.accessor("hwVersion", {
      id: "hwVersion",
      header: "Hardware Ver.",
      cell: (info) => {
        const value = info.getValue();
        const hwVersion = getHwVersionLabel({ hwVer: value });
        return <>{hwVersion}</>
      },
    }),
    columnHelper.accessor("devices", {
      id: "devices",
      header: "Devices",
      cell: (info) => {
        const devices = info.getValue();
        const len = devices ? devices?.length : 0;

        return (
          <Typography
            sx={{
              color: theme.palette.primary.main,
              fontSize: theme.typography.customFontSize[2],
              cursor: "pointer",
              textDecoration: "underline",
            }}
            onClick={() => callbacks?.handelSelectedDevices && callbacks.handelSelectedDevices(devices)}
          >
            {len}
          </Typography>
        )
      },
    }),
    columnHelper.accessor(row => row.firmwareAvailabilitiesLabel ?? "-", {
      id: "firmwareAvailabilitiesLabel",
      header: "Update Status",
      cell: (info) => <>{info.getValue()}</>
    }),
    columnHelper.accessor(row => row.firmwareVersion ?? "-", {
      id: "firmwareVersion",
      header: "Site FW Ver.",
      cell: (info) => <EllipsisTooltipCell text={info.getValue() ?? "-"} />,
    }),
    columnHelper.accessor(row => row.latestFirmwareVersion ?? "-", {
      id: "latestFirmwareVersion",
      header: "Latest FW Ver.",
      cell: (info) => {
        const value = info.getValue();
        const data = info.row.original;

        if (value === "-") return <Box>{value}</Box>

        return (
          <Box
            sx={{
              color: theme.palette.primary.main,
              fontSize: theme.typography.customFontSize[2],
              cursor: data?.latestFirmwareVersionHasReleaseNote ? "pointer" : "default",
              textDecoration: data?.latestFirmwareVersionHasReleaseNote ? "underline" : "none",
            }}
            onClick={() => {
              data?.latestFirmwareVersionHasReleaseNote &&
                callbacks?.handelSelectedTargetFwVer &&
                callbacks.handelSelectedTargetFwVer(data);
            }}
          >
            <EllipsisTooltipCell text={value} />
          </Box>
        )
      },
    }),
    columnHelper.accessor(row => row.targetFirmwareScheduleDateLabel ?? "-", {
      id: "targetFirmwareSchedule",
      header: () => (
        <Box>
          <Box>Auto-Update</Box>
          <Box sx={{ fontSize: theme.typography.customFontSize[1] }}>(Site Local Time)</Box>
        </Box>
      ),
      cell: (info) => {
        const value = info.getValue();

        if (value === "-") return <Box>{value}</Box>

        return (
          <Box sx={{ display: "flex", gap: 0.5, justifyContent: "flex-start", whiteSpace: "nowrap" }}>
            <Box sx={{ display: "flex", alignItems: "center" }}><DownloadIcon /></Box>
            <Box>{value}</Box>
          </Box>
        )
      },
    }),
    columnHelper.accessor(row => row.lastUpgradeTimeDateLabel ?? "-", {
      id: "lastUpgradeTime",
      header: () => (
        <Box>
          <Box>Last Update Time</Box>
          <Box sx={{ fontSize: theme.typography.customFontSize[1] }}>(Site Local Time)</Box>
        </Box>
      ),
      cell: (info) => {
        const value = info.getValue();
        return <>{value}</>
      },
    }),
  ].filter(Boolean);
}

export const getDevicesModalColumn = ({
  columnHelper,
}) => {
  return [
    columnHelper.accessor(row => row.deviceName ?? "-", {
      id: "deviceName",
      header: "Device Name",
      cell: (info) => <>{info.getValue()}</>,
    }),
    columnHelper.accessor(row => row.modelName ?? "-", {
      id: "modelName",
      header: "Model Name",
      cell: (info) => <>{info.getValue()}</>,
    }),
    columnHelper.accessor(row => row.hwVersion ?? "-", {
      id: "hwVersion",
      header: "Hardware Ver.",
      cell: (info) => <>{info.getValue()}</>,
    }),
    columnHelper.accessor(row => row.firmwareAvailabilitiesLabel ?? "-", {
      id: "firmwareAvailabilitiesLabel",
      header: "Update Status",
      cell: (info) => <>{info.getValue()}</>,
    }),
    columnHelper.accessor(row => row.firmwareVersion ?? "-", {
      id: "firmwareVersion",
      header: "Firmware Ver.",
      cell: (info) => <EllipsisTooltipCell text={info.getValue()} />,
    }),
  ].filter(Boolean);
}

export const getUpdateModalColumn = ({
  columnHelper,
  openedAction,
  theme,
  callbacks,
  isNext
}) => {
  const isCancelUpdateMode = openedAction.key === "cancelUpdate";

  return [
    columnHelper.accessor(row => row.siteName ?? "-", {
      header: "Site",
      cell: (info) => <>{info.getValue()}</>,
    }),
    columnHelper.accessor("deviceType", {
      header: "Product Category",
      cell: (info) => {
        const value = info.getValue();
        const productCategory = productCategories?.[value]?.label ?? value ?? "-";
        return <>{productCategory}</>
      },
    }),
    columnHelper.accessor(row => row.seriesName ?? "-", {
      header: "Series Name",
      cell: (info) => <>{info.getValue()}</>,
    }),
    columnHelper.accessor("hwVersion", {
      header: "Hardware Ver.",
      cell: (info) => {
        const value = info.getValue();
        const hwVersion = getHwVersionLabel({ hwVer: value });
        return <>{hwVersion}</>
      },
    }),
    columnHelper.accessor(row => row.firmwareVersion ?? "-", {
      header: "Site FW Ver.",
      cell: (info) => <EllipsisTooltipCell text={info.getValue()} />,
    }),
    (
      isCancelUpdateMode ?
        columnHelper.accessor("targetFirmwareSchedule", {
          header: () => (
            <Box>
              <Box>Auto-Update</Box>
              <Box>(Site Local Time)</Box>
            </Box>
          ),
          cell: (info) => <Typography sx={{ fontWeight: "bold" }}>{info.getValue() ? dayjs.unix(info.getValue()).format("YYYY/MM/DD h:mm A") : "-"}</Typography>,
        }) :
        columnHelper.accessor("targetFirmwareVersion", {
          header: "Target FW Ver.",
          cell: (info) => {
            const value = info.getValue();
            const data = info.row.original;
            const firmwareList = data.firmwareList.map((fw, index) => {
              return {
                ...fw,
                value: fw.version,
                label: fw.version + (index === 0 ? " (Latest)" : "")
              }
            })

            if (!isNext) {
              return (
                <MenuSelect
                  placeholder="Select a FW Ver."
                  width="170px"
                  value={value}
                  options={firmwareList}
                  onChange={(opt) => {
                    const changedTargetFw = {
                      ...cloneDeep(data),
                      targetFirmwareVersion: opt.version,
                      firmwareList: data.firmwareList.map(fw => {
                        return {
                          ...cloneDeep(fw),
                          isSelected: fw.id === opt.id
                        }
                      })
                    }

                    callbacks.handleTargetFwVers(changedTargetFw);
                  }}
                />
              )
            } else {
              return (
                <Box sx={{ padding: "3.79px 0", boxSizing: "borderBox" }}>
                  <EllipsisTooltipCell text={value} />
                </Box>
              )
            }
          },
        })
    ),
    columnHelper.accessor("devices", {
      header: "Devices",
      cell: (info) => {
        const devices = info.getValue();
        const len = devices ? devices?.length : 0;
        return <>{len}</>;
      },
    }),
  ]
}

export const getSupportListModalColumn = ({
  columnHelper,
  callbacks
}) => {
  return [
    columnHelper.accessor(row => row.serialName ?? "-", {
      id: "serialName",
      header: "Series Name",
      cell: (info) => <>{info.getValue()}</>,
    }),
    columnHelper.accessor(row => row.hwVersion ?? "-", {
      id: "hwVersion",
      header: "Hardware Ver.",
      cell: (info) => {
        const value = info.getValue();
        const hwVersion = getHwVersionLabel({ hwVer: value });
        return <>{hwVersion}</>
      },
    }),
    columnHelper.accessor(row => row.version ?? "-", {
      id: "version",
      header: "Firmware Ver.",
      cell: (info) => {
        const value = info.getValue();
        const data = info.row.original;

        return (
          <Box
            sx={
              {
                display: "flex",
                alignItems: "center",
                gap: 1,
                cursor: data?.hasReleaseNote ? "pointer" : "default",
                textDecoration: data?.hasReleaseNote ? "underline" : "none",
              }
            }
            onClick={() => {
              data?.hasReleaseNote &&
                callbacks?.handelSelectedTargetFwVer &&
                callbacks.handelSelectedTargetFwVer(data);
            }}
          >
            <Box sx={{ display: "flex", gap: 1, overflow: "hidden" }}>
              <Box sx={{ display: "flex", alignItems: "center" }}><DownloadIcon /></Box>
              <Box sx={{ minWidth: 0, flex: 1 }}><EllipsisTooltipCell text={value} /></Box>
            </Box>
          </Box>
        )
      },
    }),
    columnHelper.accessor(row => row.releaseDateYMD ?? "-", {
      id: "releaseDateYMD",
      header: "Release Date",
      cell: (info) => <>{info.getValue()}</>,
    }),
  ].filter(Boolean);
}